import { MILESTONES, MONEY_BONUS, MONEY_GIFTS, shopItem } from "./data/items.js";
import { FORM_AREAS, refreshForm, settleFormPause } from "./form.js";
import {
  activeMute,
  applyRested,
  baseXp,
  bodyStage,
  earnedMilestones,
  goldForXp,
  levels,
  matchesQuest,
  normalizeText,
  repeatCount,
  restedPool,
  xpForLevel,
} from "./rules.js";
import {
  HOUR,
  addDays,
  atGameDay,
  dayKey,
  gameDayStart,
  isDayKey,
  localMinutes,
  nextDayStart,
  weekKey,
  type DayKey,
} from "./time.js";
import {
  AREAS,
  GROWTH_AREAS,
  LIMITS,
  isGrowthArea,
  type ActivityAnimation,
  type Area,
  type ChatMessage,
  type CompanionActivity,
  type DeviceProfile,
  type GameState,
  type Look,
  type Mode,
  type MoneyTier,
  type MuteKind,
  type NudgeType,
  type PromptKind,
  type PromptOutcome,
  type Quest,
  type Settings,
  type Size,
} from "./types.js";

/** How long the companion keeps doing what he announced, when the model gives no duration. */
export const DEFAULT_COMPANION_MINUTES: Record<CompanionActivity, number> = {
  sleep: 480,
  nap: 30,
  read: 20,
  train: 10,
  cook: 15,
  eat: 10,
  clean: 10,
  work: 30,
  play: 20,
  sit: 15,
  celebrate: 1,
  walk_out: 30,
  shopping_out: 25,
};

export const DEFAULT_SETTINGS: Settings = {
  companionName: "Giermek",
  userName: "",
  timezone: "Europe/Warsaw",
  briefFrom: "07:00",
  briefUntil: "12:00",
  debriefFrom: "21:00",
  activeFrom: "10:00",
  activeUntil: "21:00",
  eveningAt: "19:00",
  maxNudgesPerDay: 3,
  nightFrom: "23:00",
  nightUntil: "06:30",
  comeHomeAfterHours: 0,
  llmDailyLimit: 200,
  workOn: true,
  workFrom: "09:00",
  workUntil: "17:00",
};

export const DEFAULT_LOOK: Look = { skin: 1, hair: 0, hairColor: 1, beard: 0, glasses: false, shirt: 0 };

export function createInitialState(now: number, settings: Partial<Settings> = {}): GameState {
  const s = { ...DEFAULT_SETTINGS, ...settings };
  return {
    version: 1,
    createdAt: now,
    onboarded: false,
    settings: s,
    look: { ...DEFAULT_LOOK },
    stats: {
      xp: { body: 0, mind: 0, work: 0, social: 0, home: 0 },
      gold: 0,
      rested: 0,
      lastActiveDay: null,
      bodyStage: 0,
      form: {
        body: { value: 1, day: dayKey(now, s.timezone), paused: 0 },
        mind: { value: 1, day: dayKey(now, s.timezone), paused: 0 },
        social: { value: 1, day: dayKey(now, s.timezone), paused: 0 },
      },
    },
    mode: { kind: "normal", since: dayKey(now, s.timezone), until: null },
    mute: null,
    quests: [],
    reminders: [],
    facts: [],
    shopping: [],
    inventory: [],
    ratings: {},
    nudge: {
      day: null,
      count: 0,
      lastAt: 0,
      lastPromptAt: 0,
      lastBriefDay: null,
      lastDebriefDay: null,
      lastWeeklyWeek: null,
      returnPending: false,
      skipBriefDay: null,
      stats: {},
      variant: 0,
      lastType: null,
      lastPrompt: null,
    },
    recent: [],
    messages: [],
    location: { deviceId: null, deviceName: "", profile: "wall", since: now },
    devices: [],
    llm: { day: null, calls: 0, inputTokens: 0, outputTokens: 0, totalCalls: 0 },
    seq: 1,
  };
}

export type Action =
  | { type: "log_activity"; area: Area; text: string; size: Size; animation?: ActivityAnimation }
  | {
      type: "add_quest";
      title: string;
      area: Area;
      size: Size;
      due?: DayKey | "today" | "tomorrow" | null;
      repeatDays?: number | null;
    }
  | { type: "complete_quest"; id: string }
  | { type: "postpone_quest"; id: string; to?: DayKey | null }
  | { type: "drop_quest"; id: string }
  | { type: "add_reminder"; text: string; at: number }
  | { type: "reminder_fire"; id: string }
  | { type: "reminder_ack"; id: string; outcome: "done" | "hour" | "tomorrow" }
  | { type: "set_mode"; mode: Mode; until?: DayKey | null }
  | { type: "mute"; kind: MuteKind; untilDay?: DayKey | null }
  | { type: "unmute" }
  | { type: "money_event"; tier: MoneyTier }
  | { type: "buy_item"; itemId: string }
  | { type: "remember_fact"; text: string }
  | { type: "forget_fact"; id: string }
  | { type: "shopping_add"; items: string[] }
  | { type: "shopping_remove"; items: string[] }
  | { type: "shopping_toggle"; id: string }
  | { type: "shopping_clear" }
  | { type: "rate_week"; value: number }
  | { type: "companion_do"; activity: CompanionActivity; minutes?: number | null }
  /** Debug mode (Settings): test levels, gold, furniture and mess without real activities. Only adds. */
  | {
      type: "debug";
      op: "xp" | "gold" | "item" | "mess" | "form";
      area?: Area;
      amount?: number;
      itemId?: string;
      level?: number;
    }
  | { type: "update_settings"; patch: Partial<Settings> }
  | { type: "set_look"; look: Look }
  | {
      type: "onboard";
      look: Look;
      companionName?: string;
      userName?: string;
      timezone?: string;
      /** Starting level per growth area (1–10) from the first-run questions: nobody starts from zero. */
      start?: Partial<Record<Area, number>>;
    }
  | {
      type: "prompt_shown";
      kind: PromptKind;
      nudgeType?: NudgeType | null;
      questId?: string | null;
      reminderId?: string | null;
      /** What the companion said; kept in chat history so the model knows what it asked. */
      text?: string;
    }
  | { type: "prompt_outcome"; outcome: PromptOutcome }
  | { type: "set_location"; deviceId: string; deviceName: string; profile: DeviceProfile }
  | { type: "device_seen"; id: string; name: string; profile: DeviceProfile; visible: boolean }
  | { type: "chat"; role: ChatMessage["role"]; text: string }
  | { type: "llm_usage"; inputTokens: number; outputTokens: number };

export type GameEvent =
  | {
      type: "xp";
      area: Area;
      xp: number;
      bonus: number;
      gold: number;
      text: string;
      animation: ActivityAnimation;
      questId: string | null;
    }
  | { type: "level_up"; area: Area; level: number }
  | { type: "body_stage"; stage: number }
  | { type: "item"; itemId: string; source: "milestone" | "shop" | "gift" }
  | { type: "gold"; amount: number; reason: "money" }
  | { type: "celebrate"; reason: "money" | "level" | "item" | "week" }
  | { type: "mode"; mode: Mode; until: DayKey | null }
  | { type: "mute"; kind: MuteKind; until: number }
  | { type: "unmute" }
  | { type: "quest_added"; id: string; title: string }
  | { type: "quest_closed"; id: string; status: "done" | "dropped" }
  | { type: "reminder_added"; id: string; at: number; text: string }
  | { type: "reminder_due"; id: string; text: string }
  | { type: "shopping" }
  | { type: "location"; from: string | null; to: string; toName: string }
  | { type: "companion"; activity: CompanionActivity; until: number };

export interface ApplyResult {
  state: GameState;
  events: GameEvent[];
  /** Short machine-readable outcome, fed back to the LLM as the tool result. */
  result: string;
  ok: boolean;
}

const clip = (s: string, n: number = LIMITS.textLen) => s.trim().replace(/\s+/g, " ").slice(0, n);

function nextId(state: GameState, prefix: string): string {
  const id = `${prefix}${state.seq}`;
  state.seq += 1;
  return id;
}

/** Done for today: a repeating quest moves to its next due day, a one-off one closes. */
function closeQuest(q: Quest, events: GameEvent[], today: DayKey): void {
  q.lastDoneDay = today;
  if (q.repeatDays) {
    q.dueDay = addDays(today, q.repeatDays);
    q.postponed = 0;
    return;
  }
  q.status = "done";
  q.closedDay = today;
  events.push({ type: "quest_closed", id: q.id, status: "done" });
}

export function guessAnimation(area: Area, text: string): ActivityAnimation {
  const t = normalizeText(text);
  if (/piw|\bbar\b|\bpub|drink/.test(t)) return "beer";
  if (/kaw|kawiar|herbat/.test(t)) return "coffee";
  if (area === "social" && /spacer|przechadz|park/.test(t)) return "walk";
  if (/śmie|smie|worek|kosz/.test(t)) return "trash";
  if (/gotow|obiad|kolac|śniad|sniad|jedz|zjad|ugotow/.test(t)) return "cook";
  if (/zakup|sklep|kupi/.test(t)) return "shop";
  if (/sprząt|sprzat|odkurz|umy|pranie|zmyw|porząd|porzad/.test(t)) return "clean";
  if (/pisa|notes|mail|list/.test(t)) return "write";
  if (/czyt|książ|ksiaz|rozdzia/.test(t)) return "read";
  switch (area) {
    case "body":
      return "train";
    case "mind":
      return "read";
    case "work":
      return "work";
    case "social":
      return "coffee";
    case "home":
      return "clean";
  }
}

/** Housekeeping: expire modes and mutes, prune bounded collections. Idempotent. */
export function normalize(input: GameState, now: number): GameState {
  const state = input;
  const tz = state.settings.timezone;
  const today = dayKey(now, tz);

  if (state.mode.kind !== "normal" && state.mode.until && today >= state.mode.until) {
    state.mode = { kind: "normal", since: today, until: null };
  }
  if (state.mute && now >= state.mute.until) {
    if (state.mute.kind === "days") state.nudge.returnPending = true;
    state.mute = null;
  }

  const minDay = addDays(today, -(LIMITS.recentDays - 1));
  if (state.recent.length && (state.recent[0]!.day < minDay || state.recent.length > LIMITS.recentMax)) {
    state.recent = state.recent.filter((a) => a.day >= minDay).slice(-LIMITS.recentMax);
  }
  const closed = state.quests.filter((q) => q.status !== "open");
  if (closed.length > LIMITS.closedQuests) {
    const keep = new Set(closed.slice(-LIMITS.closedQuests).map((q) => q.id));
    state.quests = state.quests.filter((q) => q.status === "open" || keep.has(q.id));
  }
  const done = state.reminders.filter((r) => r.status === "done");
  if (done.length > LIMITS.doneReminders) {
    const keep = new Set(done.slice(-LIMITS.doneReminders).map((r) => r.id));
    state.reminders = state.reminders.filter((r) => r.status !== "done" || keep.has(r.id));
  }
  const weeks = Object.keys(state.ratings).sort();
  if (weeks.length > LIMITS.ratings) {
    for (const w of weeks.slice(0, weeks.length - LIMITS.ratings)) delete state.ratings[w];
  }
  if (state.messages.length > LIMITS.messages) state.messages = state.messages.slice(-LIMITS.messages);
  return state;
}

/** Resolve the end of a mute. */
export function muteUntil(kind: MuteKind, now: number, tz: string, untilDay?: DayKey | null): number {
  switch (kind) {
    case "short":
      return now + 3 * HOUR;
    case "today":
      return nextDayStart(now, tz);
    case "days": {
      const today = dayKey(now, tz);
      const day = untilDay && isDayKey(untilDay) && untilDay > today ? untilDay : addDays(today, 3);
      return gameDayStart(day, tz);
    }
  }
}

function award(
  state: GameState,
  events: GameEvent[],
  now: number,
  area: Area,
  size: Size,
  text: string,
  animation: ActivityAnimation,
  questId: string | null,
) {
  const tz = state.settings.timezone;
  const today = dayKey(now, tz);
  const before = levels(state.stats.xp);
  const repeats = repeatCount(state.recent, today, area, text, questId);
  const base = baseXp(size, repeats);
  // Home chores pay the same gold but never use the rest bonus (it is for growth).
  const r = isGrowthArea(area)
    ? applyRested(base, restedPool(state.stats, today))
    : { xp: base, bonus: 0, pool: state.stats.rested };
  const gold = goldForXp(r.xp);
  state.stats.xp[area] += r.xp;
  state.stats.gold += gold;
  state.stats.rested = r.pool;
  state.stats.lastActiveDay = today;
  refreshForm(state, area, size, today);
  const activity = {
    id: nextId(state, "a"),
    at: now,
    day: today,
    area,
    text: clip(text),
    size,
    xp: r.xp,
    bonus: r.bonus,
    gold,
    animation,
    questId,
  };
  state.recent.push(activity);
  events.push({ type: "xp", area, xp: r.xp, bonus: r.bonus, gold, text: activity.text, animation, questId });

  const after = levels(state.stats.xp);
  for (const a of GROWTH_AREAS)
    if (after[a] > before[a]) events.push({ type: "level_up", area: a, level: after[a] });
  const stage = bodyStage(after.body);
  if (stage > state.stats.bodyStage) {
    state.stats.bodyStage = stage;
    events.push({ type: "body_stage", stage });
  }
  grantMilestones(state, events, now);
  return activity;
}

export function grantMilestones(state: GameState, events: GameEvent[], now: number) {
  const owned = new Set(state.inventory.map((i) => i.id));
  for (const id of earnedMilestones(levels(state.stats.xp))) {
    if (owned.has(id)) continue;
    state.inventory.push({ id, at: now, source: "milestone" });
    events.push({ type: "item", itemId: id, source: "milestone" });
  }
}

const findQuest = (state: GameState, id: string) =>
  state.quests.find((q) => q.id === id && q.status === "open");

function resolveDue(due: string | null | undefined, today: DayKey): DayKey {
  if (due === "tomorrow") return addDays(today, 1);
  if (due && isDayKey(due) && due >= today) return due;
  return today;
}

/**
 * Apply one action. Mutates a structural copy, never the input.
 * Unknown ids and invalid input produce ok=false with a reason, never a throw.
 */
export function applyAction(input: GameState, action: Action, now: number): ApplyResult {
  const state = normalize(structuredClone(input), now);
  const events: GameEvent[] = [];
  const tz = state.settings.timezone;
  const today = dayKey(now, tz);
  const ok = (result: string): ApplyResult => ({ state, events, result, ok: true });
  const fail = (result: string): ApplyResult => ({ state: input, events: [], result, ok: false });

  switch (action.type) {
    case "log_activity": {
      if (!AREAS.includes(action.area)) return fail("bad_area");
      const text = clip(action.text || "aktywność");
      const anim = action.animation ?? guessAnimation(action.area, text);
      // The model sometimes logs what was a quest instead of completing it: close that quest too.
      const q = state.quests.find(
        (x) =>
          x.status === "open" &&
          x.area === action.area &&
          x.dueDay <= today &&
          x.lastDoneDay !== today &&
          matchesQuest(x.title, text),
      );
      const a = award(state, events, now, action.area, action.size, text, anim, q?.id ?? null);
      if (q) closeQuest(q, events, today);
      return ok(`logged ${a.id} +${a.xp}xp${q ? `, closed quest ${q.id}` : ""}`);
    }
    case "add_quest": {
      const title = clip(action.title, 80);
      if (!title) return fail("empty_title");
      if (state.quests.filter((q) => q.status === "open").length >= LIMITS.openQuests)
        return fail("too_many_quests");
      const dup = state.quests.find(
        (q) => q.status === "open" && normalizeText(q.title) === normalizeText(title),
      );
      if (dup) return ok(`exists ${dup.id}`);
      const rep = Number(action.repeatDays);
      const quest = {
        id: nextId(state, "q"),
        title,
        area: AREAS.includes(action.area) ? action.area : "home",
        size: action.size,
        status: "open" as const,
        createdDay: today,
        dueDay: resolveDue(action.due, today),
        repeatDays: rep > 0 ? Math.min(60, Math.round(rep)) : null,
        postponed: 0,
        lastDoneDay: null,
        closedDay: null,
      };
      state.quests.push(quest);
      events.push({ type: "quest_added", id: quest.id, title });
      return ok(`quest ${quest.id}`);
    }
    case "complete_quest": {
      const q = findQuest(state, action.id);
      if (!q) return fail("unknown_quest");
      award(state, events, now, q.area, q.size, q.title, guessAnimation(q.area, q.title), q.id);
      closeQuest(q, events, today);
      return ok(`done ${q.id}`);
    }
    case "postpone_quest": {
      const q = findQuest(state, action.id);
      if (!q) return fail("unknown_quest");
      const to = action.to && isDayKey(action.to) && action.to > today ? action.to : addDays(today, 1);
      q.dueDay = to;
      q.postponed += 1;
      return ok(`postponed ${q.id} to ${to}${q.postponed >= 3 ? " (often postponed, maybe too big)" : ""}`);
    }
    case "drop_quest": {
      const q = findQuest(state, action.id);
      if (!q) return fail("unknown_quest");
      q.status = "dropped";
      q.closedDay = today;
      events.push({ type: "quest_closed", id: q.id, status: "dropped" });
      return ok(`dropped ${q.id}`);
    }
    case "add_reminder": {
      const text = clip(action.text, 100);
      if (!text) return fail("empty_text");
      if (!Number.isFinite(action.at)) return fail("bad_time");
      if (state.reminders.filter((r) => r.status !== "done").length >= LIMITS.pendingReminders)
        return fail("too_many_reminders");
      const at = Math.max(action.at, now + 60_000);
      const r = {
        id: nextId(state, "r"),
        text,
        at,
        status: "pending" as const,
        createdAt: now,
        firedAt: null,
      };
      state.reminders.push(r);
      events.push({ type: "reminder_added", id: r.id, at, text });
      return ok(`reminder ${r.id}`);
    }
    case "reminder_fire": {
      const r = state.reminders.find((x) => x.id === action.id && x.status === "pending");
      if (!r) return fail("unknown_reminder");
      r.status = "due";
      r.firedAt = now;
      events.push({ type: "reminder_due", id: r.id, text: r.text });
      return ok(`due ${r.id}`);
    }
    case "reminder_ack": {
      const r = state.reminders.find((x) => x.id === action.id && x.status !== "done");
      if (!r) return fail("unknown_reminder");
      if (action.outcome === "done") {
        r.status = "done";
      } else if (action.outcome === "hour") {
        r.status = "pending";
        r.at = now + HOUR;
      } else {
        r.status = "pending";
        r.at = atGameDay(addDays(today, 1), localMinutes(r.at, tz), tz);
      }
      return ok(`${action.outcome} ${r.id}`);
    }
    case "set_mode": {
      settleFormPause(state, today);
      const until = action.until && isDayKey(action.until) && action.until > today ? action.until : null;
      state.mode = { kind: action.mode, since: today, until: action.mode === "normal" ? null : until };
      events.push({ type: "mode", mode: action.mode, until: state.mode.until });
      return ok(`mode ${action.mode}`);
    }
    case "mute": {
      const until = muteUntil(action.kind, now, tz, action.untilDay);
      const late = localMinutes(now, tz) >= 22 * 60 || localMinutes(now, tz) < 4 * 60;
      state.mute = { kind: action.kind, since: now, until };
      if (action.kind === "today" && late) state.nudge.skipBriefDay = dayKey(until, tz);
      state.nudge.returnPending = false;
      events.push({ type: "mute", kind: action.kind, until });
      return ok(`muted ${action.kind} until ${new Date(until).toISOString()}`);
    }
    case "unmute": {
      const was = activeMute(state.mute, now);
      state.mute = null;
      state.nudge.returnPending = false;
      state.nudge.skipBriefDay = null;
      events.push({ type: "unmute" });
      return ok(was ? "unmuted" : "was_not_muted");
    }
    case "money_event": {
      const amount = MONEY_BONUS[action.tier];
      state.stats.gold += amount;
      events.push({ type: "gold", amount, reason: "money" });
      events.push({ type: "celebrate", reason: "money" });
      if (action.tier === "large") {
        const gift = MONEY_GIFTS.find((id) => !state.inventory.some((i) => i.id === id));
        if (gift) {
          state.inventory.push({ id: gift, at: now, source: "gift" });
          events.push({ type: "item", itemId: gift, source: "gift" });
        }
      }
      return ok(`gold +${amount}`);
    }
    case "debug": {
      const amount = Math.max(0, Math.min(100_000, Math.round(Number(action.amount ?? 0))));
      if (action.op === "xp") {
        const area = action.area && AREAS.includes(action.area) ? action.area : null;
        if (!area) return fail("bad_area");
        const before = levels(state.stats.xp);
        state.stats.xp[area] += amount;
        const after = levels(state.stats.xp);
        if (isGrowthArea(area) && after[area] > before[area])
          events.push({ type: "level_up", area, level: after[area] });
        const stage = bodyStage(after.body);
        if (stage > state.stats.bodyStage) {
          state.stats.bodyStage = stage;
          events.push({ type: "body_stage", stage });
        }
        grantMilestones(state, events, now);
        return ok(`debug xp ${area} +${amount}`);
      }
      if (action.op === "form") {
        const area = FORM_AREAS.find((a) => a === action.area);
        if (!area) return fail("bad_area");
        const value = Math.max(0, Math.min(100, Math.round(Number(action.amount ?? 100)))) / 100;
        // level = days since the last activity, to test the decay.
        const ago = Math.max(0, Math.min(365, Math.round(Number(action.level ?? 0))));
        state.stats.form[area] = { value, day: addDays(today, -ago), paused: 0 };
        return ok(`debug form ${area} ${value}`);
      }
      if (action.op === "gold") {
        state.stats.gold += amount;
        return ok(`debug gold +${amount}`);
      }
      if (action.op === "item") {
        const id = action.itemId ?? "";
        const known = MILESTONES.some((m) => m.id === id) || Boolean(shopItem(id));
        if (!known) return fail("unknown_item");
        if (state.inventory.some((i) => i.id === id)) return ok("already_owned");
        state.inventory.push({ id, at: now, source: "shop" });
        events.push({ type: "item", itemId: id, source: "shop" });
        return ok(`debug item ${id}`);
      }
      // Mess comes from overdue home quests: replace the debug ones with `level` overdue chores.
      const level = Math.max(0, Math.min(3, Math.round(Number(action.level ?? 0))));
      state.quests = state.quests.filter((q) => !(q.status === "open" && q.title.startsWith("[debug]")));
      for (let i = 0; i < level; i++)
        state.quests.push({
          id: nextId(state, "q"),
          title: `[debug] bałagan ${i + 1}`,
          area: "home",
          size: "S",
          status: "open",
          createdDay: addDays(today, -1),
          dueDay: addDays(today, -1),
          repeatDays: null,
          postponed: 0,
          lastDoneDay: null,
          closedDay: null,
        });
      return ok(`debug mess ${level}`);
    }
    case "buy_item": {
      const item = shopItem(action.itemId);
      if (!item) return fail("unknown_item");
      if (state.inventory.some((i) => i.id === item.id)) return fail("already_owned");
      if (state.stats.gold < item.price)
        return fail(`not_enough_gold have ${state.stats.gold} need ${item.price}`);
      state.stats.gold -= item.price;
      state.inventory.push({ id: item.id, at: now, source: "shop" });
      events.push({ type: "item", itemId: item.id, source: "shop" });
      return ok(`bought ${item.id}`);
    }
    case "remember_fact": {
      const text = clip(action.text, 140);
      if (!text) return fail("empty_fact");
      if (state.facts.some((f) => normalizeText(f.text) === normalizeText(text))) return ok("already_known");
      state.facts.push({ id: nextId(state, "f"), text, at: now });
      if (state.facts.length > LIMITS.facts) state.facts = state.facts.slice(-LIMITS.facts);
      return ok("remembered");
    }
    case "forget_fact": {
      const before = state.facts.length;
      state.facts = state.facts.filter((f) => f.id !== action.id);
      return before === state.facts.length ? fail("unknown_fact") : ok("forgotten");
    }
    case "shopping_add": {
      let added = 0;
      for (const raw of action.items) {
        const text = clip(String(raw), 40);
        if (!text) continue;
        const existing = state.shopping.find((s) => normalizeText(s.text) === normalizeText(text));
        if (existing) {
          existing.done = false;
          continue;
        }
        if (state.shopping.length >= LIMITS.shopping) break;
        state.shopping.push({ id: nextId(state, "s"), text, done: false, at: now });
        added++;
      }
      events.push({ type: "shopping" });
      return ok(`shopping +${added}`);
    }
    case "shopping_remove": {
      const drop = new Set(action.items.map((i) => normalizeText(String(i))));
      state.shopping = state.shopping.filter((s) => !drop.has(normalizeText(s.text)));
      events.push({ type: "shopping" });
      return ok("shopping removed");
    }
    case "shopping_toggle": {
      const s = state.shopping.find((x) => x.id === action.id);
      if (!s) return fail("unknown_item");
      s.done = !s.done;
      events.push({ type: "shopping" });
      return ok("toggled");
    }
    case "shopping_clear": {
      state.shopping = [];
      events.push({ type: "shopping" });
      return ok("shopping cleared");
    }
    case "rate_week": {
      const v = Math.round(Number(action.value));
      if (!(v >= 1 && v <= 5)) return fail("bad_rating");
      state.ratings[weekKey(today)] = v;
      if (state.nudge.lastPrompt?.kind === "weekly") state.nudge.lastPrompt = null;
      return ok(`rated ${v}`);
    }
    case "companion_do": {
      const minutes = Math.round(Number(action.minutes ?? DEFAULT_COMPANION_MINUTES[action.activity]));
      if (!(minutes >= 1 && minutes <= 720)) return fail("bad_minutes");
      events.push({ type: "companion", activity: action.activity, until: now + minutes * 60_000 });
      return ok(`companion ${action.activity} ${minutes} min`);
    }
    case "update_settings": {
      state.settings = sanitizeSettings({ ...state.settings, ...action.patch });
      return ok("settings");
    }
    case "set_look": {
      state.look = sanitizeLook(action.look);
      return ok("look");
    }
    case "onboard": {
      state.look = sanitizeLook(action.look);
      state.settings = sanitizeSettings({
        ...state.settings,
        companionName: action.companionName ?? state.settings.companionName,
        userName: action.userName ?? state.settings.userName,
        timezone: action.timezone ?? state.settings.timezone,
      });
      for (const area of GROWTH_AREAS) {
        const level = Math.round(Number(action.start?.[area] ?? 1));
        if (!(level > 1)) continue;
        state.stats.xp[area] = Math.max(state.stats.xp[area], xpForLevel(Math.min(10, level)));
      }
      // Furniture and look match the starting point right away, without level-up fanfare.
      state.stats.bodyStage = Math.max(state.stats.bodyStage, bodyStage(levels(state.stats.xp).body));
      grantMilestones(state, [], now);
      state.onboarded = true;
      return ok("onboarded");
    }
    case "prompt_shown": {
      const n = state.nudge;
      n.lastPromptAt = now;
      n.lastPrompt = {
        kind: action.kind,
        at: now,
        nudgeType: action.nudgeType ?? null,
        questId: action.questId ?? null,
        reminderId: action.reminderId ?? null,
      };
      if (action.kind === "brief") n.lastBriefDay = today;
      if (action.kind === "debrief") n.lastDebriefDay = today;
      if (action.kind === "weekly") n.lastWeeklyWeek = weekKey(today);
      if (action.kind === "return") n.returnPending = false;
      if (action.kind === "nudge") {
        n.count = n.day === today ? n.count + 1 : 1;
        n.day = today;
        n.lastAt = now;
        n.lastType = action.nudgeType ?? null;
      }
      n.variant = (n.variant + 1) % 1000;
      if (action.text) {
        state.messages.push({ role: "assistant", text: clip(action.text, 600), at: now });
        if (state.messages.length > LIMITS.messages) state.messages = state.messages.slice(-LIMITS.messages);
      }
      return ok("shown");
    }
    case "prompt_outcome": {
      const lp = state.nudge.lastPrompt;
      if (!lp) return ok("no_prompt");
      if (lp.kind === "nudge" && lp.nudgeType) {
        const key = statKey(lp.nudgeType, localMinutes(lp.at, tz));
        const c = (state.nudge.stats[key] ??= { answered: 0, ignored: 0, dismissed: 0 });
        c[action.outcome] += 1;
        // Decay so old habits don't silence a type forever.
        if (c.answered + c.ignored + c.dismissed > 20) {
          c.answered = Math.floor(c.answered / 2);
          c.ignored = Math.floor(c.ignored / 2);
          c.dismissed = Math.floor(c.dismissed / 2);
        }
      }
      if (lp.kind !== "weekly" || action.outcome !== "answered") state.nudge.lastPrompt = null;
      return ok(`outcome ${action.outcome}`);
    }
    case "set_location": {
      const from = state.location.deviceId;
      state.location = {
        deviceId: action.deviceId,
        deviceName: clip(action.deviceName, 30),
        profile: action.profile,
        since: now,
      };
      if (from !== action.deviceId)
        events.push({ type: "location", from, to: action.deviceId, toName: state.location.deviceName });
      return ok("location");
    }
    case "device_seen": {
      const name = clip(action.name, 30) || "urządzenie";
      const d = state.devices.find((x) => x.id === action.id);
      if (d) Object.assign(d, { name, profile: action.profile, lastSeen: now, visible: action.visible });
      else
        state.devices.push({
          id: action.id,
          name,
          profile: action.profile,
          lastSeen: now,
          visible: action.visible,
        });
      if (state.devices.length > LIMITS.devices) {
        state.devices.sort((a, b) => b.lastSeen - a.lastSeen);
        state.devices = state.devices.filter(
          (x, i) => i < LIMITS.devices || x.id === state.location.deviceId,
        );
      }
      if (state.location.deviceId === action.id) {
        state.location.deviceName = name;
        state.location.profile = action.profile;
      }
      return ok("device");
    }
    case "chat": {
      state.messages.push({ role: action.role, text: clip(action.text, 600), at: now });
      if (state.messages.length > LIMITS.messages) state.messages = state.messages.slice(-LIMITS.messages);
      return ok("chat");
    }
    case "llm_usage": {
      if (state.llm.day !== today) {
        state.llm.day = today;
        state.llm.calls = 0;
      }
      state.llm.calls += 1;
      state.llm.totalCalls += 1;
      state.llm.inputTokens += Math.max(0, action.inputTokens | 0);
      state.llm.outputTokens += Math.max(0, action.outputTokens | 0);
      return ok("usage");
    }
  }
}

/** Apply several actions in order; failures are reported but don't stop the batch. */
export function applyActions(
  input: GameState,
  actions: readonly Action[],
  now: number,
): { state: GameState; events: GameEvent[]; results: { action: Action; result: string; ok: boolean }[] } {
  let state = input;
  const events: GameEvent[] = [];
  const results: { action: Action; result: string; ok: boolean }[] = [];
  for (const action of actions) {
    const r = applyAction(state, action, now);
    state = r.state;
    events.push(...r.events);
    results.push({ action, result: r.result, ok: r.ok });
  }
  return { state, events, results };
}

export function statKey(type: NudgeType, minutes: number): string {
  return `${type}:${Math.floor(minutes / 120)}`;
}

export function llmCallsToday(state: GameState, now: number): number {
  return state.llm.day === dayKey(now, state.settings.timezone) ? state.llm.calls : 0;
}

const HM = /^\d{2}:\d{2}$/;

function sanitizeSettings(s: Settings): Settings {
  const d = DEFAULT_SETTINGS;
  const hm = (v: unknown, def: string) => (typeof v === "string" && HM.test(v) ? v : def);
  const num = (v: unknown, def: number, lo: number, hi: number) =>
    typeof v === "number" && Number.isFinite(v) ? Math.min(hi, Math.max(lo, Math.round(v))) : def;
  let tz = d.timezone;
  try {
    if (typeof s.timezone === "string" && s.timezone) {
      new Intl.DateTimeFormat("en-US", { timeZone: s.timezone });
      tz = s.timezone;
    }
  } catch {
    tz = d.timezone;
  }
  return {
    companionName: clip(String(s.companionName ?? d.companionName), 24) || d.companionName,
    userName: clip(String(s.userName ?? ""), 24),
    timezone: tz,
    briefFrom: hm(s.briefFrom, d.briefFrom),
    briefUntil: hm(s.briefUntil, d.briefUntil),
    debriefFrom: hm(s.debriefFrom, d.debriefFrom),
    activeFrom: hm(s.activeFrom, d.activeFrom),
    activeUntil: hm(s.activeUntil, d.activeUntil),
    eveningAt: hm(s.eveningAt, d.eveningAt),
    maxNudgesPerDay: num(s.maxNudgesPerDay, d.maxNudgesPerDay, 0, 3),
    nightFrom: hm(s.nightFrom, d.nightFrom),
    nightUntil: hm(s.nightUntil, d.nightUntil),
    comeHomeAfterHours: num(s.comeHomeAfterHours, d.comeHomeAfterHours, 0, 72),
    llmDailyLimit: num(s.llmDailyLimit, d.llmDailyLimit, 0, 5000),
    workOn: typeof s.workOn === "boolean" ? s.workOn : d.workOn,
    workFrom: hm(s.workFrom, d.workFrom),
    workUntil: hm(s.workUntil, d.workUntil),
  };
}

function sanitizeLook(l: Look): Look {
  const int = (v: unknown, max: number) =>
    typeof v === "number" && Number.isFinite(v) ? Math.min(max, Math.max(0, Math.round(v))) : 0;
  return {
    skin: int(l.skin, 4),
    hair: int(l.hair, 5),
    hairColor: int(l.hairColor, 7),
    beard: int(l.beard, 3),
    glasses: Boolean(l.glasses),
    shirt: int(l.shirt, 7),
  };
}
